import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { FileSpreadsheet, Download, X, AlertCircle, CheckCircle2 } from 'lucide-react';
import { BulkUserUploadResult } from '@/types/user';
import { cn, getFullName } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface BulkUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Backend expects Excel file with columns: userId, firstName, middleName (optional), lastName, mobile */
  onUpload: (file: File) => Promise<BulkUserUploadResult>;
}

interface ParsedUser {
  userId: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  mobile?: string;
  isValid: boolean;
  error?: string;
}

export function BulkUpload({ open, onOpenChange, onUpload }: BulkUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parsedUsers, setParsedUsers] = useState<ParsedUser[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<BulkUserUploadResult | null>(null);

  const parseExcelFile = async (file: File): Promise<ParsedUser[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as string[][];

          // Skip header row. Columns: userId, firstName, middleName (optional), lastName, mobile
          const users: ParsedUser[] = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length === 0 || !row[0]) continue;

            const userId = String(row[0] || '').trim();
            const firstName = String(row[1] || '').trim();
            const middleName = String(row[2] || '').trim() || undefined;
            const lastName = String(row[3] || '').trim();
            const mobile = String(row[4] || '').trim() || undefined;

            let isValid = true;
            let error: string | undefined;

            if (!userId || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userId)) {
              isValid = false;
              error = 'Invalid email (userId) format';
            } else if (!firstName) {
              isValid = false;
              error = 'First name is required';
            } else if (!lastName) {
              isValid = false;
              error = 'Last name is required';
            }

            users.push({ userId, firstName, middleName, lastName, mobile, isValid, error });
          }

          resolve(users);
        } catch (error) {
          reject(new Error('Failed to parse Excel file'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsBinaryString(file);
    });
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setFile(file);
    setUploadResult(null);

    try {
      const users = await parseExcelFile(file);
      setParsedUsers(users);
      
      const invalidCount = users.filter(u => !u.isValid).length;
      if (invalidCount > 0) {
        toast.warning(`${invalidCount} row(s) have validation errors`);
      }
    } catch (error) {
      toast.error('Failed to parse Excel file');
      setFile(null);
      setParsedUsers([]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    disabled: isUploading,
  });

  const handleUpload = async () => {
    if (!file) {
      toast.error('No file selected');
      return;
    }

    setIsUploading(true);
    try {
      const result = await onUpload(file);
      setUploadResult(result);

      if (result.success > 0) {
        toast.success(`Successfully created ${result.success} user(s)`);
      }
      if (result.failed > 0) {
        toast.error(`Failed to create ${result.failed} user(s)`);
      }
    } catch {
      toast.error('Failed to upload users');
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setParsedUsers([]);
    setUploadResult(null);
    onOpenChange(false);
  };

  const downloadTemplate = () => {
    const template = [
      ['userId', 'firstName', 'middleName', 'lastName', 'mobile'],
      ['john.doe@example.com', 'John', '', 'Doe', '+1234567890'],
      ['jane.smith@example.com', 'Jane', 'Marie', 'Smith', '+1987654321'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'user_upload_template.xlsx');
  };

  const validCount = parsedUsers.filter(u => u.isValid).length;
  const invalidCount = parsedUsers.filter(u => !u.isValid).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Bulk Upload Users</DialogTitle>
          <DialogDescription>
            Upload an Excel file to create multiple users at once.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Download */}
          <Button variant="outline" size="sm" onClick={downloadTemplate}>
            <Download className="mr-2 h-4 w-4" />
            Download Template
          </Button>

          {/* Dropzone */}
          {!uploadResult && (
            <div
              {...getRootProps()}
              className={cn(
                'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50',
                isUploading && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input {...getInputProps()} />
              <div className="flex flex-col items-center gap-2">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <FileSpreadsheet className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium">
                  {file ? file.name : 'Drop your Excel file here or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Supports .xlsx and .xls files
                </p>
              </div>
            </div>
          )}

          {/* Parsed Users Preview */}
          {parsedUsers.length > 0 && !uploadResult && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  Preview ({validCount} valid, {invalidCount} invalid)
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFile(null);
                    setParsedUsers([]);
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <ScrollArea className="h-48 rounded-md border">
                <div className="p-4 space-y-2">
                  {parsedUsers.map((user, index) => (
                    <div
                      key={index}
                      className={cn(
                        'flex items-center justify-between p-2 rounded text-sm',
                        user.isValid ? 'bg-green-500/10' : 'bg-red-500/10'
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {user.isValid ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span>{user.userId}</span>
                        <span className="text-muted-foreground">
                          - {getFullName(user)}
                        </span>
                      </div>
                      {user.error && (
                        <span className="text-xs text-red-500">{user.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Upload Result */}
          {uploadResult && (
            <Alert variant={uploadResult.failed > 0 ? 'destructive' : 'default'}>
              <CheckCircle2 className="h-4 w-4" />
              <AlertTitle>Upload Complete</AlertTitle>
              <AlertDescription>
                Successfully created {uploadResult.success} user(s).
                {uploadResult.failed > 0 && (
                  <>
                    <br />
                    Failed to create {uploadResult.failed} user(s).
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {uploadResult ? 'Close' : 'Cancel'}
          </Button>
          {!uploadResult && (
            <Button
              onClick={handleUpload}
              disabled={!file || isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload File'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
